/**
 * P4 status-pip resolver (pure).
 *
 * Decides what color a mech's status pip should show, given the
 * current mech state + recent-event rising edges. Priority ladder
 * (highest wins):
 *
 *   1. shutdown   → red, slow pulse
 *   2. just-hit   → red flash (mirrors `hitPulse` window)
 *   3. just-fired → white flash
 *   4. high-heat  → amber, soft pulse
 *   5. otherwise  → hidden
 *
 * Returns `null` when the pip should be invisible. The component owns
 * the imperative mesh; this module just shapes the inputs.
 */

export type PipColor = string;

export interface PipResolverInputs {
  shutdown: boolean;
  /** ms since the most recent hit landed on this side (null = none). */
  hitElapsedMs: number | null;
  /** ms since the most recent shot was fired by this side (null = none). */
  fireElapsedMs: number | null;
  /** Mech's current heat reading. */
  heat: number;
}

export interface PipState {
  color: PipColor;
  /** Scale multiplier on a unit-radius pip mesh. Drives the pulse. */
  scale: number;
}

export const PIP_HIT_FLASH_MS = 250;
export const PIP_FIRE_FLASH_MS = 200;
export const PIP_LOCAL_Y = 0.95;
export const PIP_RADIUS = 0.055;

const SHUTDOWN_PULSE_PERIOD_MS = 800;
const HEAT_PULSE_PERIOD_MS = 500;
const SHUTDOWN_PULSE_AMPLITUDE = 0.15;
const HIT_FLASH_SCALE = 1.35;
const FIRE_FLASH_SCALE = 1.2;
const HEAT_PULSE_AMPLITUDE = 0.2;

const PIP_HEAT_WARN = 20;
const PIP_HEAT_PEAK_FOR_GLOW = 30;

const COLOR_SHUTDOWN = "#ef4444"; // red
const COLOR_HIT = "#ef4444";
const COLOR_FIRE = "#ffffff";
const COLOR_HEAT = "#f59e0b"; // amber

export function resolvePipState(
  inputs: PipResolverInputs,
  nowMs: number,
  reducedMotion: boolean,
): PipState | null {
  const justHitActive = inputs.hitElapsedMs !== null && inputs.hitElapsedMs < PIP_HIT_FLASH_MS;
  const justFiredActive = inputs.fireElapsedMs !== null && inputs.fireElapsedMs < PIP_FIRE_FLASH_MS;

  if (inputs.shutdown) {
    const pulse = reducedMotion
      ? 0
      : SHUTDOWN_PULSE_AMPLITUDE *
        Math.sin((nowMs / SHUTDOWN_PULSE_PERIOD_MS) * Math.PI * 2);
    return { color: COLOR_SHUTDOWN, scale: 1 + pulse };
  }
  if (justHitActive) return { color: COLOR_HIT, scale: HIT_FLASH_SCALE };
  if (justFiredActive) return { color: COLOR_FIRE, scale: FIRE_FLASH_SCALE };
  if (inputs.heat >= PIP_HEAT_WARN) {
    const heatNorm = Math.min(
      1,
      (inputs.heat - PIP_HEAT_WARN) / (PIP_HEAT_PEAK_FOR_GLOW - PIP_HEAT_WARN),
    );
    const pulse = reducedMotion
      ? 0
      : HEAT_PULSE_AMPLITUDE *
        heatNorm *
        Math.sin((nowMs / HEAT_PULSE_PERIOD_MS) * Math.PI * 2);
    return { color: COLOR_HEAT, scale: 1 + pulse };
  }
  return null;
}
